// useShortcuts — React hook that bridges DOM keyboard events to the shortcut registry
// (Doc 06 §8). Normalises key events to strings like "space", "escape",
// "cmd+enter", "cmd+backspace", "arrowup", "arrowdown", "tab", "shift+tab".
// Then calls `registry.dispatch(key)`.
//
// Only called once at the top-level editor; dialogs push/pop their scope separately.

import { useEffect } from "react";
import type { ShortcutRegistry } from "@pack-it-up/viewmodel";

/** Normalise a DOM KeyboardEvent to a lower-case shortcut key string. */
export const normaliseKey = (e: KeyboardEvent): string | null => {
  const mod = e.metaKey || e.ctrlKey;
  const shift = e.shiftKey;
  const key = e.key;

  // Ignore events that originate from editable fields unless they're modifier combos.
  // This prevents firing shortcuts while the user is typing a name, etc.
  // Exception: modifier combos (Cmd/Ctrl+key) are allowed even in inputs because they
  // are unlikely to be regular text.
  const target = e.target as HTMLElement | null;
  const isEditable =
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target?.isContentEditable;

  if (isEditable && !mod) {
    // Allow Escape and Enter from editable fields — the row edit form uses them.
    // Also allow Tab/Shift+Tab for field navigation within edit forms.
    if (key !== "Escape" && key !== "Enter" && key !== "Tab") return null;
  }

  if (mod && key === "Enter") return "cmd+enter";
  if (mod && key === "Backspace") return "cmd+backspace";
  if (shift && key === "Tab") return "shift+tab";

  switch (key) {
    case " ": return "space";
    case "Escape": return "escape";
    case "Enter": return "enter";
    case "Tab": return "tab";
    case "ArrowUp": return "arrowup";
    case "ArrowDown": return "arrowdown";
    default: return null;
  }
};

export const useShortcuts = (registry: ShortcutRegistry): void => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      const key = normaliseKey(e);
      if (key === null) return;
      const handled = registry.dispatch(key);
      if (handled) {
        // Only prevent default for keys that have side-effects we want to suppress
        // (e.g. Space scrolling the page, Tab moving browser focus outside the app).
        if (key === "space" || key === "tab" || key === "shift+tab") {
          e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [registry]);
};
